/*
  The Wrapped gift app renders its launcher button inside an open shadow root
  (<wrapped-gift-block>), so theme CSS cannot reach it. This injects a
  stylesheet into that shadow root to recolour the "Add Gift Bag?" launcher on
  the product page - white background, black text and black checkbox outline.
  (The checkbox is an inline SVG whose fill is currentColor.)

  Why this is needed rather than just configuring the app: Wrapped exposes a
  single "primary button" colour control, and it drives BOTH the confirm button
  inside the gift modal AND the "Add Gift Bag?" launcher on the product page -
  they share --shadow-primary-button-surface / --shadow-primary-button-text-color.
  So the app can only ever make both of them solid blue with white text.

  We want them to differ: a solid blue button in the modal (where it is the
  main call to action), but a subtle checkbox-style launcher on the product
  page, so it does not compete with "Add to Basket". Setting the shared custom
  properties on the host would change the modal button too, so the launcher is
  overridden on its own here instead.

  Once a gift bag has been added the app sets [with-gift-options] on the
  launcher (that is what makes its tick appear), so that is used to flip the
  launcher green - the white/black version is easy to miss after the modal
  closes, so it needs to read as "added".
*/
;(() => {
  const productTemplate = document
    .querySelector('html')
    .classList.contains('template-product')

  if (!productTemplate) return

  const HOST_SELECTOR = 'wrapped-gift-block[target-group="product-page"]'

  const CSS = `
    .gift-block__launcher,
    .gift-block__launcher:hover,
    .gift-block__launcher:active,
    .gift-block__launcher:focus {
      background-color: #ffffff !important;
      color: #000000 !important;
    }

    .gift-block__launcher[with-gift-options],
    .gift-block__launcher[with-gift-options]:hover,
    .gift-block__launcher[with-gift-options]:active,
    .gift-block__launcher[with-gift-options]:focus {
      background-color: #72c17f !important;
    }

    .gift-block__launcher .icon-wrapper svg,
    .gift-block__launcher .checkmark-box,
    .gift-block__launcher .checkmark-icon {
      fill: #000000 !important;
    }
  `

  let sheet = null

  if (typeof CSSStyleSheet !== 'undefined' && 'replaceSync' in CSSStyleSheet.prototype) {
    sheet = new CSSStyleSheet()
    sheet.replaceSync(CSS)
  }

  const styleShadowRoot = (host) => {
    const shadowRoot = host?.shadowRoot

    if (!shadowRoot) return false

    //adoptedStyleSheets survives the app re-rendering its own markup
    if (sheet) {
      if (shadowRoot.adoptedStyleSheets.includes(sheet)) return true
      shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet]
      return true
    }

    //fallback for browsers without constructable stylesheets
    if (shadowRoot.querySelector('style[data-theme-gift-block]')) return true
    const styleElement = document.createElement('style')
    styleElement.setAttribute('data-theme-gift-block', '')
    styleElement.textContent = CSS
    shadowRoot.appendChild(styleElement)
    return true
  }

  //the shadow root is attached when the custom element upgrades, which can
  //happen after this script runs, so poll briefly then keep watching the DOM
  const styleAllHosts = () => {
    const hosts = [...document.querySelectorAll(HOST_SELECTOR)]
    return hosts.length > 0 && hosts.every((host) => styleShadowRoot(host))
  }

  const pollForHosts = () => {
    let attempts = 0
    const interval = setInterval(() => {
      attempts++
      if (styleAllHosts() || attempts > 40) clearInterval(interval)
    }, 250)
  }

  customElements.whenDefined('wrapped-gift-block').then(styleAllHosts)
  pollForHosts()

  //re-apply if the app tears down and re-inserts the block (eg. ajax updates)
  let queued = false
  new MutationObserver(() => {
    if (queued) return
    queued = true
    requestAnimationFrame(() => {
      queued = false
      styleAllHosts()
    })
  }).observe(document.body, {
    childList: true,
    subtree: true
  })
})()
